import type { ResolvePhoneNumbersReturn } from '../../resolver/phoneNumberResolver';
import { MobXProviderContext } from 'mobx-react';
import type { TransferPhoneNumber, TransferTarget } from 'Modules/softphone/types';
import { useContext, useEffect, useMemo } from 'react';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { formatNumber } from 'Utils/phoneUtil';
import { resolvePhoneNumbers } from '../../resolver/phoneNumberResolver';

export interface UseTransferContactNumbersReturn extends ResolvePhoneNumbersReturn {
  /** The pre-formatted display string for the currently selected phone. */
  displayValue: string | undefined;
}

/**
 * Formats the display text for a phone entry.
 * "Office" numbers are shown raw (they are extensions); others are formatted.
 */
function formatPhoneDisplay(phone: TransferPhoneNumber): string {
  return phone.label === 'Office' ? phone.number : formatNumber(phone.number);
}

/**
 * Resolves the phone number list for a transfer target and auto-selects the
 * first number once available.
 *
 * Uses the personStore to reactively resolve fresh contact data at confirmation
 * time, ensuring phone numbers are always up-to-date. Falls back to the
 * pre-processed phoneNumbers from the search step when the contact is not yet
 * loaded in the store.
 */
export function useTransferContactNumbers(
  transferTarget: TransferTarget | null,
  selectedPhoneNumber: string | null,
  onPhoneNumberChange: (number: string) => void
): UseTransferContactNumbersReturn {
  const { person: personStore } = useContext<RootStoreProps>(MobXProviderContext);
  const result = resolvePhoneNumbers(transferTarget, personStore);

  useEffect(() => {
    if (result.isLoaded && result.phoneNumbers.length > 0 && !selectedPhoneNumber) {
      onPhoneNumberChange(result.phoneNumbers[0].number);
    }
  }, [result.isLoaded, result.phoneNumbers, selectedPhoneNumber, onPhoneNumberChange]);

  const displayValue = useMemo(() => {
    const selectedPhone = result.phoneNumbers.find(
      (p) => p.number === selectedPhoneNumber
    );

    if (selectedPhone) {
      return `${selectedPhone.label}: ${formatPhoneDisplay(selectedPhone)}`;
    }

    return selectedPhoneNumber ? formatNumber(selectedPhoneNumber) : undefined;
  }, [result.phoneNumbers, selectedPhoneNumber]);

  return { ...result, displayValue };
}
